import { usePlaybackVideoJsPlayer } from '@app/core/playback/video/useVideoPlaybackState';
import { generateWebAppSignedUrl } from '@app/modules/fragments/lib/generate-web-app-signed-url';
import { useEffect, useMemo } from 'react';
import { defineMessage, useIntl } from 'react-intl';
import Player from 'video.js/dist/types/player';

const subtitlesTrackLabelMessage = defineMessage({
  id: 'fragments.video.subtitlesTrackLabel',
  defaultMessage: 'Subtítulos',
});

/**
 * Attaches WebVTT subtitles to the Video.js instance from the surrounding VideoPlaybackProvider.
 * Uses the BFF Kubb client `generateUrl` so the track loads a signed GET URL (no Blob fetch).
 * Mount only when subtitles should be active (parent gates e.g. transcription completed).
 * @param srclang - BCP-47 language for `TextTrack` metadata (typically the station language).
 *
 * Cue timestamps must match the clip timeline (fragment-relative vs full stream). Validate
 * with real media if captions appear early/late.
 */
export function useFragmentVideoJsSubtitles(fragmentId: string, srclang: string) {
  const { $t } = useIntl();
  const trackLabel = $t(subtitlesTrackLabelMessage);
  const player = usePlaybackVideoJsPlayer();

  const subtitlesSrc = useMemo(() => {
    if (!fragmentId) {
      return null;
    }
    return generateWebAppSignedUrl(`/web-app/fragments/${fragmentId}/subtitles`, {
      format: 'vtt',
    });
  }, [fragmentId]);

  useEffect(() => {
    if (!subtitlesSrc || !player || player.isDisposed()) {
      return;
    }

    let cancelled = false;
    let trackElement: ReturnType<Player['addRemoteTextTrack']> | undefined;

    const attach = () => {
      if (cancelled || player.isDisposed()) {
        return;
      }
      trackElement = player.addRemoteTextTrack(
        {
          kind: 'subtitles',
          src: subtitlesSrc,
          srclang,
          label: trackLabel,
        },
        true,
      );
    };

    const onReady = () => {
      if (cancelled || player.isDisposed()) {
        return;
      }
      if (player.readyState() >= 1) {
        attach();
      } else {
        player.one('loadedmetadata', attach);
      }
    };

    player.ready(onReady);

    return () => {
      cancelled = true;
      player.off('loadedmetadata', attach);
      if (trackElement && !player.isDisposed()) {
        player.removeRemoteTextTrack(trackElement);
      }
    };
  }, [subtitlesSrc, player, trackLabel, srclang]);
}
